// @ts-strict-ignore
import { isConnectionSpanData, ISpanData } from './span-data';
import { LineSpanInfo } from './line-span-info';
import { ArrayUtils } from '@shared/util/array-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { StringUtils } from '@shared/util/string-utils';

export enum TableProviderType {
  DEFAULT = 'DEFAULT',
  MARKING_CARD = 'MARKING_CARD',
  DIRECT_PRINTING = 'DIRECT_PRINTING',
  SEPARATE_LABELING = 'SEPARATE_LABELING',
  LABELING_CATEGORY = 'LABELING_CATEGORY',
}

export interface TableProviderTabLabel {
  label: string;
  labelingCategoryTranslateKey?: string;
}

export interface ITableProvider {
  id: string;
  readonly type: TableProviderType;
  readonly rowCount: number;
  readonly colCount: number;
  readonly table: ISpanData[][];
  // lineSpans: each entry in this list determines how many rows make up a single line.
  // A "line" contains complete label materials one after the other.
  lineSpans: number[]; // replaces previously uniformly used common baselineDistance
  separateLabelingPositionNumber: string;
  separateLabelingArticleNumber: string;
  tabLabel: TableProviderTabLabel;
  tabTooltipText: string;
  containsErrors: boolean;
  containsStrictConformityErrors: boolean;

  getSpanData(rowIndex: number, colIndex: number): ISpanData;
  getRow(rowIndex: number): ISpanData[];
  getColumnHeader(colIndex: number): string;
  getColumnHeaderTooltip(colIndex: number): string;
  getMinPositionOnRail(): number;
  getMaxPositionOnRail(): number;
  getUnusedLabelingSpanData(): ISpanData[];

  // TODO: performance might increase here with uilizing caches for following methods:
  getLineSpanInfoForRow(rowIndex: number): LineSpanInfo;
  getMaxMultilineCount(rowIndex: number, colIndex: number): number;
  getMaxAvailableMultilineCount(): number;

  activate(): void;
  deactivate(): void;
  adjustRowAndColSpanAccordingToPosInTable(): void;
  updateTextCheckErrorsState(): void;
}

export class TableProvider implements ITableProvider {
  private _id: string = null;
  private _type: TableProviderType = null;
  private _table: ISpanData[][] = null;
  private _columnHeaderTooltips: string[] = null;
  private _lineSpans: number[] = [];
  private _positionsOnRail: number[] = null;
  private _separateLabelingPositionNumber: string = null;
  private _separateLabelingArticleNunber: string = null;
  private _tabLabel: TableProviderTabLabel = null;
  private _tabTooltipText: string = null;
  private _containsErrors: boolean = false;
  private _containsStrictConformityErrors: boolean = false;

  constructor(
    private containerService: ContainerService,
    type: TableProviderType = TableProviderType.DEFAULT,
    table: ISpanData[][] = null,
    lineSpans: number[] = null,
    columnHeaderTooltips: string[] = null
  ) {
    this._id = StringUtils.createUuid();
    this._type = type;
    this._table = table;
    this._lineSpans = lineSpans === null ? [] : lineSpans;
    this._columnHeaderTooltips = columnHeaderTooltips;
    this.setPositionsOnRail();
    this.adjustRowAndColSpanAccordingToPosInTable();
    this.updateTableSpanDataInformation(this._table);
  }

  public activate(): void {
    if (this._table === null || this._table.length === 0) {
      return;
    }
    for (let i: number = 0; i < this._table.length; i++) {
      const row: ISpanData[] = this._table[i];
      for (let j: number = 0; j < row.length; j++) {
        const spanData: ISpanData = row[j];
        if (spanData) {
          spanData.activate();
        }
      }
    }
  }

  public deactivate(): void {
    if (this._table === null || this._table.length === 0) {
      return;
    }
    for (let i: number = 0; i < this._table.length; i++) {
      const row: ISpanData[] = this._table[i];
      for (let j: number = 0; j < row.length; j++) {
        const spanData: ISpanData = row[j];
        if (spanData) {
          spanData.deactivate();
        }
      }
    }
  }

  public adjustRowAndColSpanAccordingToPosInTable(): void {
    if (this._table === null || this._table.length === 0) {
      return;
    }
    const colCount: number = this.colCount;
    const rowCount: number = this.rowCount;
    for (let i: number = 0; i < this._table.length; i++) {
      const row: ISpanData[] = this._table[i];
      for (let j: number = 0; j < row.length; j++) {
        const spanData: ISpanData = row[j];
        if (spanData) {
          spanData.adjustRowAndColSpanAccordingToPosInTable(i, j, rowCount, colCount);
        }
      }
    }
  }

  public get id(): string {
    return this._id;
  }

  public get type(): TableProviderType {
    return this._type;
  }

  public get lineSpans(): number[] {
    return this._lineSpans;
  }

  public set lineSpans(value: number[]) {
    this._lineSpans = value;
    this.updateTableSpanDataInformation(this._table);
  }

  public get table(): ISpanData[][] {
    return this._table;
  }

  public get rowCount(): number {
    if (this._table === null) {
      return 0;
    }
    return this._table.length;
  }

  public get colCount(): number {
    if (this._table === null || this._table.length === 0 || this._table[0] === null) {
      return 0;
    }
    let maxColCount: number = this._table[0].length;
    for (let i: number = 1; i < this._table.length; i++) {
      if (maxColCount < this._table[i].length) {
        maxColCount = this._table[i].length;
      }
    }
    return maxColCount;
  }

  public get separateLabelingPositionNumber(): string {
    return this._separateLabelingPositionNumber;
  }

  public set separateLabelingPositionNumber(position: string) {
    this._separateLabelingPositionNumber = position;
  }

  public get separateLabelingArticleNumber(): string {
    return this._separateLabelingArticleNunber;
  }

  public set separateLabelingArticleNumber(articleNo: string) {
    this._separateLabelingArticleNunber = articleNo;
  }

  public get tabLabel(): TableProviderTabLabel {
    return this._tabLabel;
  }

  public set tabLabel(tabLabel: TableProviderTabLabel) {
    this._tabLabel = tabLabel;
  }

  public get tabTooltipText(): string {
    return this._tabTooltipText ? this._tabTooltipText : '';
  }

  public set tabTooltipText(tabTooltipText: string) {
    this._tabTooltipText = tabTooltipText;
  }

  public get containsErrors(): boolean {
    return this._containsErrors;
  }

  public set containsErrors(value: boolean) {
    this._containsErrors = value;
  }

  public get containsStrictConformityErrors(): boolean {
    return this._containsStrictConformityErrors;
  }

  public set containsStrictConformityErrors(value: boolean) {
    this._containsStrictConformityErrors = value;
  }

  public getSpanData(rowIndex: number, colIndex: number): ISpanData {
    const row: ISpanData[] = this.getRow(rowIndex);
    if (!row || colIndex < 0 || colIndex >= row.length) {
      return null;
    }
    return row[colIndex];
  }

  public getRow(rowIndex: number): ISpanData[] {
    if (this._table == null || rowIndex < 0 || rowIndex >= this._table.length) {
      return null;
    }
    return this._table[rowIndex];
  }

  public getColumnHeader(colIndex: number): string {
    if (!this._positionsOnRail || !this._positionsOnRail[colIndex]) {
      return '';
    }
    return this._positionsOnRail[colIndex] + '';
  }

  public getColumnHeaderTooltip(colIndex: number): string {
    if (!this._columnHeaderTooltips || !this._columnHeaderTooltips[colIndex]) {
      return null;
    }
    return this._columnHeaderTooltips[colIndex];
  }

  public getLineSpanInfoForRow(rowIndex: number): LineSpanInfo {
    if (rowIndex < 0) {
      return null;
    }
    const lineSpans: number[] = this.lineSpans;
    if (lineSpans === null || lineSpans.length === 0) {
      return new LineSpanInfo(rowIndex, 1);
    }
    let previousRow: number = 0;
    let currentRow: number = 0;
    let currentLineSpansIndex: number = 0;
    let lineSpanCount: number = 1;
    while (currentRow <= rowIndex) {
      if (currentLineSpansIndex < lineSpans.length) {
        lineSpanCount = lineSpans[currentLineSpansIndex];
      } else {
        return new LineSpanInfo(rowIndex, 1);
      }
      previousRow = currentRow;
      currentRow += lineSpanCount;
      currentLineSpansIndex++;
    }
    return new LineSpanInfo(previousRow, lineSpanCount);
  }

  public getMaxMultilineCount(rowIndex: number, colIndex: number): number {
    if (rowIndex < 0 || colIndex < 0) {
      return NaN;
    }
    const lineSpanInfo: LineSpanInfo = this.getLineSpanInfoForRow(rowIndex);
    let spanData: ISpanData = null;
    if (lineSpanInfo === null) {
      spanData = this.getSpanData(rowIndex, colIndex);
    } else {
      spanData = this.getSpanData(lineSpanInfo.startRow, colIndex);
    }
    return spanData === null ? NaN : spanData.maxMultilineCount;
  }

  public getMaxAvailableMultilineCount(): number {
    let maxAvailableMultilineCount: number = 1;
    if (this.table) {
      for (let i = 0; i < this.table.length; i++) {
        const row: ISpanData[] = this.table[i];
        if (row) {
          for (let j = 0; j < row.length; j++) {
            const maxMultilineCount: number = this.getMaxMultilineCount(i, j);
            if (!isNaN(maxMultilineCount)) {
              maxAvailableMultilineCount = Math.max(maxAvailableMultilineCount, maxMultilineCount);
            }
          }
        }
      }
    }
    return maxAvailableMultilineCount;
  }

  private updateTableSpanDataInformation(table: ISpanData[][]): void {
    if (!table) {
      return;
    }
    for (let i: number = 0; i < this._table.length; i++) {
      const row: ISpanData[] = this._table[i];
      const lineSpanInfo: LineSpanInfo = this.getLineSpanInfoForRow(i);
      this.updateRowSpanDataInformation(row, lineSpanInfo);
    }
  }

  private updateRowSpanDataInformation(row: ISpanData[], lineSpanInfo: LineSpanInfo): void {
    for (let j: number = 0; j < row.length; j++) {
      const mainSpanData: ISpanData = lineSpanInfo ? this.getSpanData(lineSpanInfo.startRow, j) : null;
      const spanData: ISpanData = row[j];
      if (spanData) {
        if (mainSpanData) {
          spanData.maxMultilineCount = mainSpanData.maxMultilineCount;
        }
        if (spanData.parent && spanData.textCheckClass) {
          spanData.isTextCheckDirty = true;
        }
        spanData.currentMultilineCount = !lineSpanInfo ? spanData.maxMultilineCount : lineSpanInfo.lineSpan / spanData.rowSpan;
      }
    }
  }

  private setPositionsOnRail(): void {
    if (this._table === null || this._table.length === 0) {
      return;
    }
    const positionsOnRail: number[] = [];
    let maxRowLength: number = 0;
    this._table.forEach((row) => {
      if (row.length > maxRowLength) {
        maxRowLength = row.length;
      }
    });
    for (let i = 0; i < maxRowLength; i++) {
      let positionOnRail: number = null;
      for (const row of this._table) {
        const spanData: ISpanData = row[i];
        if (spanData && isConnectionSpanData(spanData) && spanData.terminalPositionOnRail) {
          positionOnRail = spanData.terminalPositionOnRail;
          break;
        }
      }
      positionsOnRail.push(positionOnRail);
    }
    if (positionsOnRail.map((position) => !!position).length) {
      this._positionsOnRail = positionsOnRail;
    }
  }

  public getMinPositionOnRail(): number {
    return this.getMinOrMaxPositionOnRail();
  }

  public getMaxPositionOnRail(): number {
    return this.getMinOrMaxPositionOnRail(false);
  }

  private getMinOrMaxPositionOnRail(getMin: boolean = true): number {
    if (!this._positionsOnRail) {
      return NaN;
    }
    const positionsOnRailValues: number[] = this._positionsOnRail.filter((position: number) => !!position);
    if (ArrayUtils.isEmpty(positionsOnRailValues)) {
      return NaN;
    }
    return getMin ? Math.min(...positionsOnRailValues) : Math.max(...positionsOnRailValues);
  }

  public updateTextCheckErrorsState(): void {
    if (this._table === null || this._table.length === 0) {
      return;
    }
    let containsErrors: boolean = false;
    let containsStrictConformityErrors: boolean = false;
    for (let i: number = 0; i < this._table.length; i++) {
      const row: ISpanData[] = this._table[i];
      for (let j: number = 0; j < row.length; j++) {
        const spanData: ISpanData = row[j];
        if (spanData.enabled && !spanData.parent) {
          containsErrors = containsErrors || spanData.containsErrors;
          containsStrictConformityErrors = containsStrictConformityErrors || spanData.containsStrictConformityErrors;
          if (containsErrors && containsStrictConformityErrors) {
            break;
          }
        }
      }
      if (containsErrors && containsStrictConformityErrors) {
        break;
      }
    }
    this._containsErrors = containsErrors;
    this._containsStrictConformityErrors = containsStrictConformityErrors;
  }

  public getUnusedLabelingSpanData(): ISpanData[] {
    if (this._table === null || this._table.length === 0) {
      return null;
    }
    const unusedLabelingSpanData: ISpanData[] = [];
    for (const row of this._table) {
      for (const spanData of row) {
        if (
          isConnectionSpanData(spanData) &&
          !this.containerService.findArticleContainerInGlobal(spanData.connection.articleContainer.articleUsage.id)
        ) {
          unusedLabelingSpanData.push(spanData);
        }
      }
    }
    return unusedLabelingSpanData;
  }
}
